import { useQuery } from '@tanstack/react-query'
import type {
  CardManagementBillingContactResponse,
  CardManagementCompanyLineResponse,
} from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import {
  cardManagementActionReasonCode,
  isCardManagementActionAvailable,
} from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import { physicalCardManagementQueries } from 'src/queries/physicalCardManagement/physicalCardManagement.queries'

/**
 * Per-group load state for the Card Details panel.
 *
 * Unlike `CardOverviewStatsGroupState` (`loading | ready | demo`) this carries a
 * real `unavailable` and `error`, and deliberately does NOT reuse it — the stats
 * roll-up collapses denial and failure into `demo` only because it has a
 * fabricated fallback to show either way. Billing contact and company line have
 * no such fallback, so a denied capability and a failed request are genuinely
 * distinguishable here and both must be visible. See the plan's §8.
 */
export type CardDetailsGroupState = 'loading' | 'ready' | 'unavailable' | 'error'

export type CardOverviewCardDetailsData = {
  /** Defined only when `billingState` is `ready`. */
  billingContact: CardManagementBillingContactResponse | undefined
  billingState: CardDetailsGroupState
  /**
   * The server's denial `reasonCode` when `billingState` is `unavailable`, so
   * the panel can say "ask an Admin" for `ROLE_NOT_ALLOWED` and stay generic
   * otherwise. `undefined` in every other state.
   */
  billingReasonCode: string | undefined
  /** Defined only when `companyLineState` is `ready`. */
  companyLine: CardManagementCompanyLineResponse | undefined
  companyLineState: CardDetailsGroupState
  companyLineReasonCode: string | undefined
}

/**
 * Single data seam for the Card Details panel (EX-40630): the billing-contact
 * and company-line reads, each gated on its OWN capability, plus the per-group
 * state machine the panel branches on.
 *
 * Both reads already exist (`physicalCardManagementQueries.billingContact` /
 * `.companyLine`, shipped in PR #7412) — this hook only composes them; it adds
 * no network function, query factory, or model.
 *
 * Grouped INDEPENDENTLY on purpose: a denied `READ_PHYSICAL_CARD_COMPANY_LINE`
 * must not blank the billing rows, and a denied `READ_PHYSICAL_CARD_BILLING`
 * must not blank the company line. Capabilities come from the shared
 * `capabilities` query already fetched by `useCardOverviewStatsData` on this tab;
 * React Query dedupes on the shared key.
 *
 * @param creditCardId - The card whose billing/company line to read. Required in
 *   the type as well as in spirit: an absent id disables the capability query,
 *   and a disabled query never resolves, so the gate below would sit at
 *   `loading` forever with no terminal state.
 */
export const useCardOverviewCardDetailsData = (
  creditCardId: string,
): CardOverviewCardDetailsData => {
  // Shared with `useCardOverviewStatsData` on this tab — `retry: false` from the
  // factory is deliberate, a denied capability is an answer, not a flake.
  const capabilitiesQuery = useQuery(
    physicalCardManagementQueries.capabilities(creditCardId),
  )

  // `undefined` while the capability verdict is pending, which is what holds a
  // group at `loading` rather than flashing `unavailable` before the answer
  // lands. Keyed off `capabilitiesQuery.data` rather than the shared helper so
  // "no verdict yet" and "verdict says no" stay distinguishable.
  const billingAllowed = capabilitiesQuery.data
    ? isCardManagementActionAvailable(
        capabilitiesQuery.data.capabilities,
        'READ_PHYSICAL_CARD_BILLING',
      )
    : undefined
  const companyLineAllowed = capabilitiesQuery.data
    ? isCardManagementActionAvailable(
        capabilitiesQuery.data.capabilities,
        'READ_PHYSICAL_CARD_COMPANY_LINE',
      )
    : undefined

  const billingContactQuery = useQuery({
    ...physicalCardManagementQueries.billingContact(creditCardId),
    // A spread override REPLACES the factory's arg-presence guard, so it is
    // re-stated. Declarative `enabled` rather than an effect: a denied
    // capability must never fire a request the backend will reject.
    enabled: Boolean(creditCardId) && billingAllowed === true,
  })
  const companyLineQuery = useQuery({
    ...physicalCardManagementQueries.companyLine(creditCardId),
    enabled: Boolean(creditCardId) && companyLineAllowed === true,
  })

  const groupState = (
    allowed: boolean | undefined,
    hasData: boolean,
    isError: boolean,
  ): CardDetailsGroupState => {
    // A failed capability read means we cannot tell whether the actor may see
    // this at all — honestly an error, not a denial.
    if (capabilitiesQuery.isError) return 'error'
    if (allowed === undefined) return 'loading'
    if (!allowed) return 'unavailable'
    if (hasData) return 'ready'
    if (isError) return 'error'
    return 'loading'
  }

  const billingState = groupState(
    billingAllowed,
    billingContactQuery.data !== undefined,
    billingContactQuery.isError,
  )
  const companyLineState = groupState(
    companyLineAllowed,
    companyLineQuery.data !== undefined,
    companyLineQuery.isError,
  )

  return {
    billingContact:
      billingState === 'ready' ? billingContactQuery.data : undefined,
    billingState,
    billingReasonCode:
      billingState === 'unavailable'
        ? cardManagementActionReasonCode(
            capabilitiesQuery.data?.capabilities,
            'READ_PHYSICAL_CARD_BILLING',
          )
        : undefined,
    companyLine:
      companyLineState === 'ready' ? companyLineQuery.data : undefined,
    companyLineState,
    companyLineReasonCode:
      companyLineState === 'unavailable'
        ? cardManagementActionReasonCode(
            capabilitiesQuery.data?.capabilities,
            'READ_PHYSICAL_CARD_COMPANY_LINE',
          )
        : undefined,
  }
}
